<template>
  <v-container id="stores-table" fluid>
    <v-card outlined :disabled="loadingData">
      <v-data-table
        :headers="headers"
        :items="usersList"
        :options.sync="options"
        :footer-props="footerProps"
        :server-items-length="totalUsers"
        mobile-breakpoint="768"
        item-key="id"
        :header-props="headerProps"
        :loading="loadingData"
        dense
      >
        <template v-slot:top>
          <v-toolbar flat>
            <v-badge :content="nbActiveFilters" :value="nbActiveFilters" overlap>
              <v-btn outlined color="primary" @click="displayFilters = !displayFilters">
                <v-icon>mdi-filter-plus-outline</v-icon>
              </v-btn>
            </v-badge>
            <v-spacer></v-spacer>
            <v-btn color="primary" outlined :to="'/users/add'"><v-icon>mdi-account-plus</v-icon></v-btn>
            <v-btn color="primary" class="ml-3" outlined @click="getUserList()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>
        </template>

        <template v-slot:item.username="{ item }">
          <p :style="usernameAndEmailCSS" v-if="item.username" class="mb-0">{{ item.username }}</p>
          <span v-else>{{ $t('user.noUsername') }}</span>
        </template>

        <template v-slot:item.email="{ item }">
          <p :style="usernameAndEmailCSS" v-if="item.email" class="mb-0">{{ item.email }}</p>
          <span v-else>{{ $t('user.noEmail') }}</span>
        </template>

        <template v-slot:item.store="{ item }">
          <span v-if="item.store">{{ item.store }}</span>
          <span v-else>X</span>
        </template>

        <template v-slot:item.role="{ item }">
          <span v-if="!roleAsPopup">{{ item.role.OMS }}</span>
          <v-btn
            v-else
            text
            :class="[$vuetify.breakpoint.width >= 768 ? '' : 'pr-0']"
            color="primary"
            @click="showRole(item.role)"
          >
            {{ $t('trigger.detail') }}
          </v-btn>
        </template>

        <template v-slot:item.lastLoginInstant="{ item }">
          {{
            item.lastLoginInstant != undefined
              ? $moment(item.lastLoginInstant).format('YYYY-MM-DD HH:mm:ss')
              : $t('user.none')
          }}
        </template>

        <template v-slot:item.active="{ item }">
          <v-icon>
            {{ item.active ? 'mdi-check' : 'mdi-close' }}
          </v-icon>
        </template>

        <template v-slot:item.action="{ item }">
          <v-btn
            text
            :class="[$vuetify.breakpoint.width >= 768 ? '' : 'pr-0']"
            color="primary"
            @click="executeAction(item.id)"
          >
            {{ $t('user.edit') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    <filter-dialog
      :key="formFiltersKey"
      v-model="displayFilters"
      :title="$t('users.filterUsers')"
      :fields="formFilters"
      @confirm="filter"
      :loadingData="loadingData"
    ></filter-dialog>
    <UserRolesDetailDialog
      :title="$t('user.role')"
      :registrations="userRoles"
      v-model="displayRole"
    ></UserRolesDetailDialog>
  </v-container>
</template>

<script>
  import { mapState } from 'vuex'
  import UserRolesDetailDialog from '@/components/dialogs/UserRolesDetailDialog.vue'
  import { EventBus } from '../../event-bus'

  export default {
    name: 'UsersTable',
    components: {
      UserRolesDetailDialog,
    },
    data() {
      return {
        formFiltersKey: Date.now(),
        users: [],
        role: '',
        options: {
          itemsPerPage: 25,
        },
        totalUsers: 0,
        emailSearched: '',
        displayFilters: false,
        loadingData: false,
        filters: [],
        displayRole: false,
        userRoles: {},
      }
    },
    computed: {
      ...mapState(['currentGroup', 'isOctipasAdmin', 'partnerSettings']),
      usersList() {
        return this.users.map((user) => {
          if (user.registrations.length > 0) {
            user.role = {}
            for (let registration of user.registrations) {
              if (registration.applicationId === process.env.VUE_APP_SSO_APP_ID) {
                user.role[`OMS`] = ''
                if (registration.roles.includes('octipas-admin')) {
                  user.role[`OMS`] = this.$t('users.octipasAdmin')
                } else if (registration.roles.includes('oms-admin')) {
                  user.role[`OMS`] = this.$t('users.admin')
                } else if (registration.roles.includes('oms-customer-service')) {
                  user.role[`OMS`] = this.$t('users.customerService')
                } else if (registration.roles.includes('oms-shop-manager')) {
                  user.role[`OMS`] = this.$t('users.shopManager')
                } else if (registration.roles.includes('default-user')) {
                  user.role[`OMS`] = this.$t('users.seller')
                }
              }
              if (registration.applicationId === process.env.VUE_APP_OCTI_STOCK_SSO_APP_ID) {
                const octistockTranslation = this.$t('burgerMenu.octiStock')
                user.role[`${octistockTranslation}`] = ''
                if (registration.roles.includes('octipas-admin')) {
                  user.role[`${octistockTranslation}`] = this.$t('users.octipasAdmin')
                } else if (registration.roles.includes('general-admin')) {
                  user.role[`${octistockTranslation}`] = this.$t('users.admin')
                } else if (registration.roles.includes('general-store-manager')) {
                  user.role[`${octistockTranslation}`] = this.$t('users.generalStockManager')
                } else if (registration.roles.includes('default-manager')) {
                  user.role[`${octistockTranslation}`] = this.$t('users.defaultManager')
                } else if (registration.roles.includes('default-seller')) {
                  user.role[`${octistockTranslation}`] = this.$t('users.defaultSeller')
                }
              }
              if (registration.applicationId === process.env.VUE_APP_UNIFIED_STOCK_SSO_APP_ID) {
                const unifiedStockTranslation = this.$t('burgerMenu.unifiedStock')
                user.role[`${unifiedStockTranslation}`] = ''
                if (registration.roles.includes('octipas-admin')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.octipasAdmin')
                } else if (registration.roles.includes('admin')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.admin')
                } else if (registration.roles.includes('global-stock-manager')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.globalStockManager')
                } else if (registration.roles.includes('global-stock-viewer')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.globalStockViewer')
                } else if (registration.roles.includes('store-stock-manager')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.storeStockManager')
                } else if (registration.roles.includes('store-stock-viewer')) {
                  user.role[`${unifiedStockTranslation}`] = this.$t('users.storeStockViewer')
                }
              }
              if (registration.applicationId === process.env.VUE_APP_ANALYTICS_SSO_APP_ID) {
                const analyticsTranslation = this.$t('burgerMenu.analytics')
                user.role[`${analyticsTranslation}`] = ''
                if (registration.roles.includes('octipas-admin')) {
                  user.role[`${analyticsTranslation}`] = this.$t('users.octipasAdmin')
                } else if (registration.roles.includes('seller')) {
                  user.role[`${analyticsTranslation}`] = this.$t('users.seller')
                } else if (registration.roles.includes('manager')) {
                  user.role[`${analyticsTranslation}`] = this.$t('users.shopManager')
                } else if (registration.roles.includes('admin')) {
                  user.role[`${analyticsTranslation}`] = this.$t('users.admin')
                }
              }
            }
          }
          if (user.data) {
            user.store = user.data.userStore
          }
          return user
        })
      },
      headers() {
        if (this.$vuetify.breakpoint.width < 769) {
          return [
            {
              text: this.$t('user.username'),
              value: 'username',
              sortable: true,
            },
            {
              text: this.$t('user.email'),
              value: 'email',
              filterType: 'text',
            },
            {
              text: this.$t('user.role'),
              value: 'role',
              align: 'center',
              sortable: false,
            },
            {
              text: '',
              value: 'action',
              sortable: false,
            },
          ]
        } else {
          return [
            {
              text: this.$t('user.firstName'),
              value: 'firstName',
              sortable: false,
            },
            {
              text: this.$t('user.lastName'),
              value: 'lastName',
              sortable: false,
            },
            {
              text: this.$t('user.username'),
              value: 'username',
              sortable: true,
            },
            {
              text: this.$t('user.email'),
              value: 'email',
              filterType: 'text',
            },
            {
              text: this.$t('user.mainLocation'),
              value: 'store',
              sortable: false,
              filterType: 'text',
            },
            {
              text: this.$t('user.role'),
              value: 'role',
              align: 'center',
              sortable: false,
            },
            {
              text: this.$t('user.lastConnection'),
              value: 'lastLoginInstant',
              sortable: false,
            },
            {
              text: this.$t('user.active'),
              value: 'active',
              sortable: false,
            },
            {
              text: '',
              value: 'action',
              sortable: false,
            },
          ]
        }
      },
      formFilters() {
        let userRoles = [
          { text: this.$t('users.seller'), value: 'default-user' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
        if (this.isOctipasAdmin) {
          userRoles.push({ text: this.$t('users.octipasAdmin'), value: 'octipas-admin' })
        }
        let omsHeaderAdded = false
        if (this.partnerSettings != null) {
          if (this.partnerSettings.reception_access) {
            if (!omsHeaderAdded) {
              userRoles.unshift({ header: 'OMS' })
              omsHeaderAdded = true
            }
            userRoles.push(
              { divider: true },
              { header: this.$t('burgerMenu.octiStock') },
              { text: this.$t('users.admin'), value: 'general-admin' },
              { text: this.$t('users.generalStockManager'), value: 'general-store-manager' },
              { text: this.$t('users.defaultManager'), value: 'default-manager' },
              { text: this.$t('users.defaultSeller'), value: 'default-seller' }
            )
          }
          if (this.partnerSettings.unified_stock_access) {
            if (!omsHeaderAdded) {
              userRoles.unshift({ header: 'OMS' })
              omsHeaderAdded = true
            }

            userRoles.push(
              { divider: true },
              { header: this.$t('burgerMenu.unifiedStock') },
              { text: this.$t('users.admin'), value: 'admin' },
              { text: this.$t('users.globalStockManager'), value: 'global-stock-manager' },
              { text: this.$t('users.globalStockViewer'), value: 'global-stock-viewer' },
              { text: this.$t('users.storeStockManager'), value: 'store-stock-manager' },
              { text: this.$t('users.storeStockViewer'), value: 'store-stock-viewer' }
            )
          }
        }
        return [
          {
            text: this.$t('user.email'),
            value: 'emailSSO',
            filterType: 'text',
          },
          {
            text: this.$t('user.mainLocation'),
            value: 'store',
            filterType: 'text',
          },
          {
            text: this.$t('user.username'),
            value: 'username',
            filterType: 'text',
          },
          {
            text: this.$t('users.lastnameAndOrFirstname'),
            value: 'fullName',
            filterType: 'text',
          },
          {
            text: this.$t('user.role'),
            value: 'registrations.roles',
            filterType: 'array',
            multiple: false,
            clearable: true,
            arrayValues: userRoles,
          },
          {
            text: this.$t('user.active'),
            value: 'active',
            filterType: 'array',
            multiple: true,
            clearable: true,
            arrayValues: [
              { text: this.$t('user.active'), value: true },
              { text: this.$t('user.disabled'), value: false },
            ],
          },
        ]
      },
      footerProps() {
        return {
          'items-per-page-options': [5, 15, 25, 50, { text: 'ALL', value: 0 }],
          'items-per-page-text': this.$t('users.usersPerPage'),
        }
      },
      headerProps() {
        return {
          sortByText: this.$t('order.sortByText'),
        }
      },
      usernameAndEmailCSS() {
        if (this.$vuetify.breakpoint.width < 360) {
          return 'max-width: 150px'
        }
        if (this.$vuetify.breakpoint.width < 540) {
          return 'max-width: 200px'
        }
        if (this.$vuetify.breakpoint.width < 768) {
          return 'max-width: 345px'
        }
        if (this.$vuetify.breakpoint.width == 768) {
          return 'max-width: 200px'
        } else {
          return ''
        }
      },
      roleAsPopup() {
        let roleAsPopup = this.partnerSettings != null
        if (roleAsPopup) {
          roleAsPopup &= this.partnerSettings.reception_access || this.partnerSettings.unified_stock_access
        }
        return roleAsPopup
      },
      nbActiveFilters: function () {
        return this.filters.filter((filter) => filter.active).length
      },
    },
    mounted() {
      this.getUserList()
    },
    activated() {
      let previousPath = this.$store.state.previousPath
      if (previousPath.split('/')[1] !== 'users') {
        this.options.itemsPerPage = 25
        this.options.page = 1
        this.options.sortBy = []
        this.options.sortDesc = []
        this.formFiltersKey = Date.now()
        this.users = []
        this.filters = []
      }
      this.filter(this.filters)
    },
    watch: {
      options: {
        handler() {
          this.getUserList()
        },
      },
    },
    methods: {
      getUserList() {
        this.loadingData = true
        let myInit = this.buildMyInit()
        this.$Amplify.API.get('users', '/user/search', myInit)
          .then((response) => {
            this.users = response.users != undefined ? response.users : []
            this.totalUsers = response.nbUsers
            this.loadingData = false
            this.footerProps['items-per-page-options'].find((prop) => prop.text == 'ALL').value = this.totalUsers
          })
          .catch(() => {
            this.loadingData = false
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: 'error during users loading',
              color: 'error',
            })
          })
      },
      executeAction: function (userId) {
        this.$router.push('/users/' + userId)
      },
      filter(filters) {
        this.filters = filters
        this.getUserList()
      },
      showRole(roles) {
        this.userRoles = roles
        this.displayRole = true
      },
      buildMyInit: function () {
        let { page, itemsPerPage, sortDesc, sortBy } = this.options
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
          queryStringParameters: {
            sort_by: sortBy[0],
            sort_desc: sortDesc[0] ? 'desc' : 'asc',
            page: page,
            items_per_page: itemsPerPage,
          },
        }
        let filters = {}
        for (let filter of this.filters) {
          if (filter.active) {
            if (filter.name === 'emailSSO') {
              filters['email'] = filter.value
            } else if (filter.name === 'active') {
              // Apply "active" filter only if exactly one value (true or false) is selected
              if (filter.value.length === 1) {
                filters['active'] = filter.value
              }
            } else {
              filters[filter.name] = filter.value
            }
          }
        }
        if (Object.entries(filters).length != 0) {
          myInit.queryStringParameters.filters = JSON.stringify(filters)
        }
        return myInit
      },
    },
  }
</script>
